<template>
  <div class="m-card level-card" @click="$emit('open')">
    <div class="m-card__header">
      <div class="title-c">Nivel On Demand<MetricInfoTooltip :content="helpText" /></div>
      <div class="icon-c">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M6.67981 13.6608C6.57743 13.5529 6.49606 13.4246 6.44034 13.2833C6.38462 13.142 6.35565 12.9904 6.35507 12.8372C6.3545 12.684 6.38234 12.5321 6.437 12.3903C6.49166 12.2486 6.57207 12.1196 6.67364 12.0109L10.2636 8.1664L1.09786 8.1664C0.806693 8.1664 0.527448 8.0435 0.321558 7.82473C0.115669 7.60596 9.35902e-07 7.30925 9.79178e-07 6.99987C1.02246e-06 6.69048 0.115669 6.39377 0.321559 6.175C0.527448 5.95623 0.806693 5.83333 1.09786 5.83333L10.2636 5.83333L6.67364 1.98887C6.46854 1.76902 6.35405 1.4716 6.35533 1.16204C6.35662 0.852487 6.47359 0.556152 6.6805 0.33823C6.88741 0.120308 7.16732 -0.00135402 7.45866 1.35966e-05C7.74999 0.00138121 8.02888 0.125664 8.23397 0.34552L13.6814 6.17819C13.8855 6.39669 14 6.69204 14 6.99987C14 7.30769 13.8855 7.60304 13.6814 7.82155L8.23397 13.6542C8.1324 13.7632 8.0116 13.8499 7.87848 13.9092C7.74536 13.9685 7.60253 13.9994 7.45817 14C7.31381 14.0006 7.17076 13.971 7.0372 13.9127C6.90364 13.8545 6.78219 13.7689 6.67981 13.6608Z"
            fill="#27884D"
          />
        </svg>
      </div>
    </div>
    <div class="m-card__main">
      <div class="letter">
        <template v-if="hasLevel">{{ period.level }}</template>
        <template v-else>sin datos</template>
      </div>
      <div>
        <div class="tag-number" :class="pillClass">{{ pillText }}</div>
      </div>
    </div>
    <div class="m-card__footer">
      <div>{{ footText }}</div>
    </div>
  </div>
</template>

<script>
import MetricInfoTooltip from '@/components/Levels/MetricInfoTooltip';
import { buildOnDemandLevelHelpText } from '@/utils/onDemandLevelHelpTexts';
import {
  LEVEL_TONE_BAD,
  LEVEL_TONE_GOOD,
  LEVEL_TONE_WARNING,
  getOnDemandLevelTone
} from '@/utils/onDemandLevelTones';

const PILL_CLASSES_BY_TONE = {
  [LEVEL_TONE_GOOD]: 'gray',
  [LEVEL_TONE_WARNING]: 'orange',
  [LEVEL_TONE_BAD]: 'red'
};
const NO_ORDERS = 0;
const EMPTY = 0;
const SINGLE = 1;
const DECIMALS = 2;

export default {
  name: 'LevelCard',
  components: { MetricInfoTooltip },
  props: {
    period: { type: Object, default: null },
    periods: { type: Array, default: null },
    closedPeriods: { type: Array, default: () => [] },
    thresholds: { type: Array, default: () => [] },
    cutoffs: { type: Array, default: () => [] }
  },
  computed: {
    helpText() {
      return buildOnDemandLevelHelpText(this.cutoffs);
    },
    hasLevel() {
      return this.period !== null;
    },
    missingMetrics() {
      return this.hasLevel && Array.isArray(this.period.missing_metrics)
        ? this.period.missing_metrics
        : [];
    },
    withoutOrders() {
      return this.hasLevel && Number(this.period.orders_count) === NO_ORDERS;
    },
    pillText() {
      if (!this.hasLevel) {
        return this.periods && this.periods.length > EMPTY
          ? 'Sin nivel aún'
          : 'Sin quincenas medidas';
      }
      if (this.period.score !== null && this.period.score !== undefined) {
        return `puntaje ${Number(this.period.score).toFixed(DECIMALS)}`;
      }

      return 'Sin puntaje';
    },
    pillClass() {
      const tone = this.hasLevel ? getOnDemandLevelTone(this.period.level) : null;

      return tone ? PILL_CLASSES_BY_TONE[tone] : '';
    },
    footText() {
      if (!this.hasLevel) {
        return this.noLevelReason;
      }

      const parts = [];
      if (this.period.has_alert) {
        parts.push('con alerta');
      }
      if (this.missingMetrics.length > EMPTY) {
        parts.push(this.missingText);
      }
      if (this.withoutOrders) {
        parts.push('sin pedidos');
      }
      if (parts.length === EMPTY) {
        return 'Quincena cerrada';
      }

      return parts.join(' · ');
    },
    noLevelReason() {
      if (!this.periods || this.periods.length === EMPTY) {
        return '';
      }
      if (this.closedPeriods.length === EMPTY) {
        return 'La quincena en curso aún no cierra';
      }

      return Number(this.closedPeriods[EMPTY].orders_count) === NO_ORDERS
        ? 'La última quincena cerrada no registró pedidos'
        : 'Aún sin nivel calculado';
    },
    missingText() {
      const labels = this.missingMetrics.map(metric => {
        const threshold = this.thresholds.find(candidate => candidate.metric === metric);

        return threshold ? threshold.label.toLowerCase() : metric;
      });

      return labels.length === SINGLE
        ? `falta ${labels[EMPTY]}`
        : `faltan ${labels.join(', ')}`;
    }
  }
};
</script>
